import {
  Avatar,
  Box,
  Collapse,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography,
  Tooltip,
  Card,
  CardActions,
  CardHeader,
  CardContent,
  Grow,
} from '@mui/material'
import { darken } from '@mui/material/styles'
import React from 'react'
import { useDropzone, FileWithPath } from 'react-dropzone'
import { Delete, PhotoCamera, Visibility } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import cuid from 'cuid'
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd'
import {
  Controller,
  Control,
  UseFormTrigger,
  UseFormGetValues,
  UseFormResetField,
  UseFormSetValue,
} from 'react-hook-form'
import shallow from 'zustand/shallow'

import { useCreatePropertyAdStore } from 'store'
import { FilePathWithPreview } from 'store/CreatePropertyAdSlice/types'
import { FormValues } from '..'

interface Props {
  control: Control<FormValues>
  trigger: UseFormTrigger<FormValues>
  getValues: UseFormGetValues<FormValues>
  resetField: UseFormResetField<FormValues>
  setValue: UseFormSetValue<FormValues>
}

const StepPhotos = (props: Props) => {
  const { control, trigger, getValues, setValue } = props

  const { t } = useTranslation()

  const [uncompleteStep, invalidateStep, completeStep, validateStep] =
    useCreatePropertyAdStore(
      (state) => [
        state.uncompleteStep,
        state.invalidateStep,
        state.completeStep,
        state.validateStep,
      ],
      shallow
    )

  const [files, setFiles] = React.useState<FilePathWithPreview[]>([])

  const handleExpandClick = (file: FilePathWithPreview, index: number) => {
    setFiles((prev) => {
      const EXISTING_IMAGES = [...prev]
      EXISTING_IMAGES.splice(
        index,
        1,
        Object.assign(file, {
          expanded: !file.expanded,
        }) // only object assign keeps file attributes,not spread operator
      )
      return EXISTING_IMAGES
    })
  }

  const handleChangeLabel = (e, file: FilePathWithPreview, index: number) => {
    setFiles((prev) => {
      const EXISTING_IMAGES = [...prev]
      EXISTING_IMAGES.splice(
        index,
        1,
        Object.assign(file, {
          description: e.target.value,
        }) // only object assign keeps file attributes,not spread operator)
      )
      return EXISTING_IMAGES
    })
  }

  const onDrop = (acceptedFiles) => {
    if (acceptedFiles?.length) {
      setFiles((prev) => {
        const EXISTING_IMAGES = prev.map((item) => item?.path)
        const withoutDuplicates = acceptedFiles.filter(
          (item) => !EXISTING_IMAGES.includes(item.path)
        )
        return [
          ...prev,
          ...withoutDuplicates.map((file) =>
            Object.assign(file, {
              preview: URL.createObjectURL(file),
              description: '',
              expanded: false,
              localId: cuid(),
            })
          ),
        ]
      })
    }
  }

  const { fileRejections, getRootProps, getInputProps } = useDropzone({
    onDrop,
    maxFiles: 20,
    multiple: true,
    maxSize: 2000000,
    accept: 'image/*',
  })

  const firstRender = React.useRef<unknown>(true)

  React.useEffect(() => {
    if (firstRender.current) firstRender.current = false
    const photos = getValues('adPhotos.photos')
    setFiles(photos.map((item) => ({ ...item, localId: cuid() })))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const checkValidStatus = async () => {
    const isValid = await trigger('adPhotos.photos')
    if (!isValid) {
      uncompleteStep('adPhotos')
      invalidateStep('adPhotos')
    } else {
      completeStep('adPhotos')
      validateStep('adPhotos')
    }
  }

  React.useEffect(() => {
    if (getValues('adPhotos.photos')) {
      setValue('adPhotos.photos', files)
      checkValidStatus()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files])

  /**
   * removes photo from the list
   * @param {string} file
   */
  const removeFile = (file, e, index: number) => {
    e.preventDefault()
    e.stopPropagation()
    if (file?.preview) URL.revokeObjectURL(file?.preview)
    setFiles((prev) => {
      const EXISTING_IMAGES = [...prev]
      EXISTING_IMAGES.splice(index, 1)
      return EXISTING_IMAGES
    })
  }

  const onDragEnd = (item) => {
    const { destination, source } = item
    if (!destination) return
    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return
    }
    const old = [...files]
    const moved = old.splice(source.index, 1)
    old.splice(destination.index, 0, moved[0])
    setFiles(old)
  }

  const WIDTH = 300
  const acceptedFileItems = files.map((file, index) => (
    <Draggable key={file.localId} draggableId={file.localId} index={index}>
      {(provided, snapshot) => (
        <Card
          sx={{
            minWidth: WIDTH,
            width: '100%',
            border: '1px solid  lightgrey',
            transition: (theme) =>
              theme.transitions.create(['background-color']),
          }}
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
        >
          <CardHeader
            avatar={
              <Avatar
                sx={{
                  bgcolor: snapshot.isDragging ? 'primary.main' : 'grey',
                  color: snapshot.isDragging ? 'common.black' : 'default',
                }}
              >
                {index + 1}
              </Avatar>
            }
            action={
              <Tooltip title="View" placement="top">
                <IconButton onClick={() => handleExpandClick(file, index)}>
                  <Visibility
                    color={file?.expanded ? 'primary' : 'secondary'}
                  />
                </IconButton>
              </Tooltip>
            }
            title={file?.type === 'attachment' ? file?.fileName : file?.path}
          />
          <CardContent>
            <TextField
              onChange={(e) => handleChangeLabel(e, file, index)}
              size={'small'}
              variant="outlined"
              value={file.description}
              label={t('manageEstateAd.steps.estatePhotos.addLabel')}
              fullWidth
            />
            <Collapse in={file.expanded} timeout="auto" unmountOnExit>
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                <Box
                  component="img"
                  onClick={() =>
                    window.open(
                      file?.type === 'attachment' && file?.filePath
                        ? file?.filePath
                        : file?.preview,
                      '_blank'
                    )
                  }
                  src={
                    file?.type === 'attachment' && file?.filePath
                      ? file?.filePath
                      : file?.preview
                  }
                  alt={
                    file?.type === 'attachment' && file?.fileName
                      ? file?.fileName
                      : file?.path
                  }
                  sx={{
                    cursor: 'pointer',
                    maxHeight: { xs: 200, sm: 300, md: 400 },
                    width: 'auto',
                  }}
                />
              </Box>
            </Collapse>
          </CardContent>
          <CardActions>
            <Tooltip title="Remove" placement="top">
              <IconButton
                onClick={(e) => removeFile(file, e, index)}
                sx={{ marginLeft: 'auto' }}
              >
                <Delete color="error" />
              </IconButton>
            </Tooltip>
          </CardActions>
        </Card>
      )}
    </Draggable>
  ))

  const fileRejectionItems = fileRejections.map(
    ({ file, errors }: { file: FileWithPath; errors }) => (
      <li key={file.path}>
        {file.path} - {file.size} bytes
        <ul>
          {errors?.map((e) => (
            <Typography component="li" color="error" key={e.code}>
              {e.message}
            </Typography>
          ))}
        </ul>
      </li>
    )
  )

  return (
    <>
      <Grid container>
        <Grid item xs={12}>
          <Box>
            <Typography sx={{ marginBottom: 4, fontSize: 20 }}>
              {t('manageEstateAd.steps.estatePhotos.description')}
              {` `}
              <Typography component="span" display="inline-block" color="error">
                {` (*)`}
              </Typography>
            </Typography>
            <Controller
              control={control}
              name="adPhotos.photos"
              rules={{
                required: t(
                  'manageEstateAd.fieldRequired'
                ) as unknown as string,
              }}
              render={({ fieldState: { error } }) => (
                <Box
                  {...getRootProps()}
                  sx={{
                    border: (theme) =>
                      `2px solid ${theme.palette.primary.main}`,
                    boxSizing: 'border-box',
                    borderStyle: 'dashed',
                    borderRadius: 2.5,
                    textAlign: 'center',
                    bgcolor: 'lightgrey',
                    minHeight: 100,
                    display: 'flex',
                    flexFlow: 'column wrap',
                    justifyContent: 'center',
                    alignItems: 'center',
                    position: 'relative',
                    py: 2,
                    background: (theme) =>
                      darken(theme.palette.common.white, 0.1),
                    '&:hover': {
                      background: (theme) =>
                        darken(theme.palette.common.white, 0.2),
                      transition: (theme) =>
                        theme.transitions.create(['background-color']),
                    },
                  }}
                >
                  <Typography>
                    {t('manageEstateAd.steps.estatePhotos.title')}
                  </Typography>
                  <Typography variant="subtitle2" sx={{ whiteSpace: 'pre' }}>
                    {t('manageEstateAd.steps.estatePhotos.helper')}
                  </Typography>
                  <PhotoCamera />
                  <Box component="div">
                    {fileRejectionItems?.length ? (
                      <>
                        <Typography variant="h4">Rejected files</Typography>
                        <Box component="ul" sx={{ p: 0 }}>
                          {fileRejectionItems}
                        </Box>
                      </>
                    ) : null}
                  </Box>
                  <input type="file" hidden {...getInputProps()} />
                  <Grow in={!!error}>
                    <Typography color="error">{error?.message}</Typography>
                  </Grow>
                </Box>
              )}
            />
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Box component="div">
            {files?.length ? (
              <DragDropContext onDragEnd={onDragEnd}>
                <Typography variant="subtitle2" sx={{ py: 4 }}>
                  {t('manageEstateAd.steps.estatePhotos.sort')}
                </Typography>
                <Droppable droppableId="spourgiti_create_ad_files">
                  {(provided, snapshot) => (
                    <Stack
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      component="ul"
                      sx={{
                        p: 1,
                        border: (theme) =>
                          snapshot.isDraggingOver
                            ? `2px dashed ${theme.palette.primary.main}`
                            : 'none',
                      }}
                      spacing={1}
                    >
                      {acceptedFileItems}
                      {provided.placeholder}
                    </Stack>
                  )}
                </Droppable>
              </DragDropContext>
            ) : null}
          </Box>
        </Grid>
      </Grid>
    </>
  )
}

export default React.memo(StepPhotos)
